import { z } from 'zod';
import { pipe, Option } from 'effect';

import { storage } from '@/lib/storage';

// what the Pay split button does after a successful payment, like AP's Publish / Publish and Stay
export const PayOptionSchema = z.enum(['pay', 'pay_and_stay']);
export type PayOption = z.infer<typeof PayOptionSchema>;

export const PayOptions = PayOptionSchema.enum;

export const DEFAULT_PAY_OPTION: PayOption = PayOptions.pay;

// one entry per user; entries of other users are kept as they are when one entry is written
const StoredPayOptionsSchema = z.record(z.string(), z.unknown());
type StoredPayOptions = z.infer<typeof StoredPayOptionsSchema>;

const safeJSONParse = (raw: string | null): Option.Option<unknown> =>
  pipe(Option.fromNullable(raw), Option.flatMap(Option.liftThrowable((value: string): unknown => JSON.parse(value))));

const parseStoredPayOptions = (raw: string | null): StoredPayOptions =>
  pipe(
    safeJSONParse(raw),
    Option.flatMap((value) => Option.fromNullable(StoredPayOptionsSchema.safeParse(value).data)),
    Option.getOrElse((): StoredPayOptions => ({})),
  );

export const readPayOption = (raw: string | null, userId: string | null | undefined): PayOption =>
  pipe(
    Option.fromNullable(userId),
    Option.flatMap((id) => Option.fromNullable(parseStoredPayOptions(raw)[id])),
    Option.flatMap((value) => Option.fromNullable(PayOptionSchema.safeParse(value).data)),
    Option.getOrElse(() => DEFAULT_PAY_OPTION),
  );

export const writePayOption = (raw: string | null, userId: string, option: PayOption): string =>
  JSON.stringify({ ...parseStoredPayOptions(raw), [userId]: option });

// the option is a convenience: browser storage that throws (blocked, full) must never stop the Pay flow
const readStoredPayOptions = Option.liftThrowable(() => storage().payButtonOption().get());

export const loadPayOption = (userId: string | null | undefined): PayOption =>
  readPayOption(Option.getOrNull(readStoredPayOptions()), userId);

export const savePayOption = (userId: string | null | undefined, option: PayOption): void => {
  if (userId) {
    Option.liftThrowable(() =>
      storage()
        .payButtonOption()
        .set(writePayOption(Option.getOrNull(readStoredPayOptions()), userId, option)),
    )();
  }
};

// The navigation's next payment wraps to the first one and is the payment itself when it is alone in the list,
// so "next" exists only when it is another payment.
export const getNextPaymentId = (
  nextPaymentId: string | null | undefined,
  currentPaymentId: string | null | undefined,
): Option.Option<string> =>
  pipe(
    Option.fromNullable(nextPaymentId),
    Option.filter((id) => id !== '' && id !== currentPaymentId),
  );

export type AfterPayDestination =
  | { readonly type: 'stay' }
  | { readonly type: 'payment'; readonly paymentId: string }
  | { readonly type: 'back' };

export const resolveAfterPayDestination = (
  option: PayOption,
  nextPaymentId: Option.Option<string>,
): AfterPayDestination =>
  option === PayOptions.pay_and_stay
    ? { type: 'stay' }
    : pipe(
        nextPaymentId,
        Option.match({
          onNone: (): AfterPayDestination => ({ type: 'back' }),
          onSome: (paymentId): AfterPayDestination => ({ type: 'payment', paymentId }),
        }),
      );
